import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { AlertCircle, CheckCircle } from 'lucide-react';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (loading) return;

    setError('');
    
    try {
      const { data, error, redirectPath } = await signIn(email, password);
      
      if (error) {
        setError(error?.message || 'Error al iniciar sesión');
        return;
      }

      if (data?.user) {
        // Check if there's a return URL from protected route attempt
        const returnTo = location?.state?.from?.pathname || redirectPath || '/consumer-dashboard';
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Error inesperado al iniciar sesión');
    }
  };

  // Quick fill function for demo credentials
  const fillDemo = (userType) => {
    if (userType === 'consumer') {
      setEmail('usuario@foodloop.com');
      setPassword('usuario123');
    } else if (userType === 'business') {
      setEmail('empresa@foodloop.com');
      setPassword('empresa123');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src="/foodloop/assets/images/slazzer-preview-av4u0-1762512006110.png"
            alt="Food Loop Logo with text - Sustainable food waste reduction platform"
            className="mx-auto h-24 w-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
              console.error('Logo failed to load');
            }}
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Iniciar Sesión
          </h2>
          <p className="text-gray-600">
            Accede a tu cuenta de Food Loop
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Correo Electrónico
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="tu@email.com"
                className="mt-1"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder="••••••••"
                className="mt-1"
                required
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              <p className="text-sm text-blue-700 font-medium">Cuentas de Demostración:</p>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => fillDemo('consumer')}
                className="w-full text-left text-xs text-blue-600 hover:text-blue-800 bg-white px-3 py-2 rounded border hover:bg-blue-50 transition-colors"
                disabled={loading}
              >
                👤 <strong>Usuario:</strong> usuario@foodloop.com / usuario123
              </button>
              <button
                type="button"
                onClick={() => fillDemo('business')}
                className="w-full text-left text-xs text-blue-600 hover:text-blue-800 bg-white px-3 py-2 rounded border hover:bg-blue-50 transition-colors"
                disabled={loading}
              >
                🏪 <strong>Empresa:</strong> empresa@foodloop.com / empresa123
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </Button>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              ¿No tienes una cuenta?{' '}
              <Link to="/signup" className="font-medium text-green-600 hover:text-green-500">
                Regístrate aquí
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}